import {
  Box,
  Button,
  Empty,
  EmptyDrawerIcon,
  EmptyHeadline,
  Heading,
  HStack,
  Image,
  ShareExternalIcon,
  Skeleton,
  Text,
  VStack,
} from '@frontend/legacy-components';

import {
  LATEST_PRODUCT_UPDATES_COUNT,
  PRODUCT_UPDATES_LIST_URL,
  PRODUCT_UPDATES_UTM_PARAMS,
} from './constants';
import { getProductUpdatePreviewImageUrl } from './getProductUpdatePreviewImageUrl';
import type { LatestUpdatesProductUpdate } from './types';
import { useLatestProductUpdates } from './useLatestProductUpdates';

const PREVIEW_IMAGE_HEIGHT = 7; // 56px

const formatPublishedOn = (publishedOn: string) =>
  new Date(publishedOn).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const withUtmParams = (url: string) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${PRODUCT_UPDATES_UTM_PARAMS}`;
};

const getUpdateDescription = (update: LatestUpdatesProductUpdate) =>
  update.excerpt || update.acf?.lead_in || update.acf?.headline || null;

const ProductUpdateLink = ({
  update,
}: {
  update: LatestUpdatesProductUpdate;
}) => {
  const description = getUpdateDescription(update);
  const previewImageUrl = getProductUpdatePreviewImageUrl(update);

  return (
    <Box
      as="a"
      href={withUtmParams(update.url)}
      target="_blank"
      rel="noopener noreferrer"
      display="flex"
      flexDirection="row"
      alignItems="center"
      width="100%"
      pl={2}
      pr={2.5}
      py={1.75}
      transition="background 0.2s ease"
      _even={{ bg: 'page.plateLight' }}
      _hover={{ bg: 'interaction.surfaceHover' }}
      _active={{ bg: 'interaction.surfaceActive' }}
    >
      {previewImageUrl && (
        <Box
          flexShrink={0}
          height={PREVIEW_IMAGE_HEIGHT}
          aspectRatio="16/9"
          borderRadius="md"
          overflow="hidden"
          mr={2}
          border="1px solid"
          borderColor="separator.divider"
          boxShadow="page"
        >
          <Image
            src={previewImageUrl}
            alt=""
            width="100%"
            height="100%"
            objectFit="cover"
            objectPosition="left center"
          />
        </Box>
      )}
      <Box flexGrow={1} minW={0}>
        <HStack alignItems="baseline" spacing={1} flexWrap="wrap">
          <Text fontSize="md" fontWeight="heavy" noOfLines={2}>
            {update.title}
          </Text>
          <Text fontSize="md" fontWeight="normal" color="feature.secondary">
            {formatPublishedOn(update.published_on)}
          </Text>
        </HStack>
        {description && (
          <Text fontSize="md" noOfLines={2} mt="0.0625rem">
            {description}
          </Text>
        )}
      </Box>
      <Box margin="auto 0" ml={2} flexShrink={0}>
        <ShareExternalIcon size={3} color="feature.secondary" />
      </Box>
    </Box>
  );
};

const LatestProductUpdatesSkeleton = () => (
  <VStack spacing={0} width="100%" alignItems="stretch">
    {Array.from({ length: LATEST_PRODUCT_UPDATES_COUNT }, (_, key) => (
      <Box
        key={key}
        width="100%"
        display="flex"
        flexDirection="row"
        alignItems="center"
        pl={2}
        pr={2.5}
        py={1.75}
        _even={{ bg: 'page.plateLight' }}
      >
        <Skeleton
          height={PREVIEW_IMAGE_HEIGHT}
          aspectRatio="16/9"
          borderRadius="md"
          mr={2}
          flexShrink={0}
        />
        <Box flexGrow={1}>
          <Skeleton height={3} width="85%" mb={1} />
          <Skeleton height={2} width="95%" />
        </Box>
      </Box>
    ))}
  </VStack>
);

const LatestProductUpdatesEmpty = () => (
  <Empty size="md" py={3}>
    <EmptyDrawerIcon />
    <EmptyHeadline>No recent product updates</EmptyHeadline>
  </Empty>
);

export const LatestProductUpdates = () => {
  const { data: updates, isLoading, isError } = useLatestProductUpdates();
  const isEmpty = isError || (!isLoading && (!updates || updates.length === 0));

  return (
    <Box width="100%" pt={2}>
      <HStack alignItems="center" spacing={1} mb={1.5}>
        <Heading size="lg" fontWeight="heavy">
          Latest Product Updates
        </Heading>
        <Button
          as="a"
          href={PRODUCT_UPDATES_LIST_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="link"
          size="md"
        >
          See all
          <ShareExternalIcon size={2} ml={0.5} />
        </Button>
      </HStack>
      <Box
        border="1px solid"
        borderColor="separator.border"
        borderRadius="lg"
        bg="page.surface"
        width="100%"
        overflow="hidden"
      >
        {isLoading ? (
          <LatestProductUpdatesSkeleton />
        ) : isEmpty ? (
          <LatestProductUpdatesEmpty />
        ) : (
          <VStack spacing={0} width="100%" alignItems="stretch">
            {updates?.map((update) => (
              <ProductUpdateLink key={update.id} update={update} />
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
};
